// Hidden Live-Agent & Supervisor Console
import { useLiveMessageRead } from "@/hooks/use-live-message-read";
//
// Not surfaced in any sidebar nav — reachable only by URL (/agent-console)
// and gated by canUseAgentConsole(). Live agents, supervisors and admins
// get full action access. Members / end-users / auditors cannot reach it.
//
// Layout:
//   • Left rail   — presence toggle, queue (FIFO + claim-next + claim
//                   specific), my active sessions, and (supervisor only)
//                   an escalation queue + all-active + agent snapshot.
//   • Centre      — active conversation transcript with internal notes
//                   inline, composer + canned replies + file attach,
//                   escalate, and a disposition-on-close flow.
//   • Right rail  — visitor info + bot transcript.
//
// Real-time over /ws/live-chat:
//   queue:{org}        new/claimed/closed/escalated sessions
//   operator:{me}      my claim/close events across tabs
//   supervisor:{org}   escalations + claim/close (supervisors only)
//   session:{id}       active transcript updates
//   notes:{id}         internal-note updates (operator-only)
// Polling is kept as a low-frequency safety net if the socket drops.

import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { useLiveChatNotificationTarget } from "@/components/live-chat-notification-bell";
import { useLiveChatReconciliation } from "@/hooks/use-live-chat-reconciliation";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useAuth, isSupervisor as isSupervisorFn } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Headphones, Send, Paperclip, X, Circle, Clock, MessageSquare,
  User as UserIcon, Globe, Mail, MonitorSmartphone, ShieldAlert,
  StickyNote, Zap, ArrowRightLeft, Users, Eye, Plus, Trash2,
} from "lucide-react";
import type { LiveChatSession, LiveChatMessage, CannedReply } from "@shared/schema";

type SessionDetail = {
  session: LiveChatSession;
  messages: LiveChatMessage[];
  botTranscript: { role: string; content: string; createdAt: string | null }[];
  assignedOperator: { id: string; name: string; email: string } | null;
};

type SupervisorSession = {
  session: LiveChatSession;
  operatorName: string | null;
  escalatedByName: string | null;
};

type AgentSnapshot = {
  userId: string;
  name: string;
  status: string;
  note: string | null;
  activeCount: number;
  updatedAt: string | null;
};

type SupervisorStats = {
  queueDepth: number;
  escalatedDepth: number;
  activeCount: number;
  avgWaitSeconds: number;
  agents: AgentSnapshot[];
};

function relativeTime(iso: string | Date | null | undefined): string {
  if (!iso) return "";
  const t = typeof iso === "string" ? new Date(iso).getTime() : iso.getTime();
  const diff = Math.max(0, Date.now() - t);
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  return `${h}h ago`;
}

// Short, distinctive double-beep using the Web Audio API — no asset file
// needed and it works even when the tab is backgrounded.
function playAlertTone() {
  try {
    const Ctx = (window as any).AudioContext || (window as any).webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const beep = (start: number, freq: number) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.frequency.value = freq;
      osc.type = "sine";
      gain.gain.setValueAtTime(0.0001, ctx.currentTime + start);
      gain.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + start + 0.18);
      osc.start(ctx.currentTime + start);
      osc.stop(ctx.currentTime + start + 0.2);
    };
    beep(0, 880);
    beep(0.22, 1175);
    window.setTimeout(() => { try { ctx.close(); } catch { /* ignore */ } }, 700);
  } catch { /* ignore */ }
}

const DISPOSITION_TAGS = ["resolved", "follow-up", "billing", "technical", "complaint", "sales", "other"];
const DISPOSITION_OUTCOMES = ["resolved", "escalated", "pending", "no-response", "spam"];

export default function AgentConsolePage() {
  useLiveChatReconciliation();
  const { user, organization } = useAuth();
  const { toast } = useToast();
  const supervisor = isSupervisorFn(user);

  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [composer, setComposer] = useState("");
  const [noteDraft, setNoteDraft] = useState("");
  const [showNotes, setShowNotes] = useState(false);
  const [showCanned, setShowCanned] = useState(false);
  const [monitorOnly, setMonitorOnly] = useState(false);
  const [deptFilter, setDeptFilter] = useState<string>("");
  const target = useLiveChatNotificationTarget((id, monitor) => {
    setActiveSessionId(id);
    setMonitorOnly(monitor);
    setDeptFilter("");
  });
  const highlightedQueueRef = useRef<HTMLButtonElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const lastEscIdsRef = useRef<Set<string>>(new Set());
  const lastMsgIdsRef = useRef<Set<string>>(new Set());
  const lastMsgSessionRef = useRef<string | null>(null);

  // ── Queue + my active ───────────────────────────────────────────
  const deptListQuery = useQuery<{ id: string; name: string }[]>({
    queryKey: ["/api/departments/list"],
  });
  const deptName = (id?: string | null) =>
    (deptListQuery.data || []).find((d) => d.id === id)?.name ?? null;
  const queueQuery = useQuery<LiveChatSession[]>({
    queryKey: ["/api/live-chat/queue", deptFilter],
    queryFn: async () => {
      const url = deptFilter
        ? `/api/live-chat/queue?departmentId=${encodeURIComponent(deptFilter)}`
        : "/api/live-chat/queue";
      return (await apiRequest("GET", url)).json();
    },
    refetchInterval: 15000,
  });
  const mineQuery = useQuery<LiveChatSession[]>({
    queryKey: ["/api/live-chat/sessions/mine"],
    refetchInterval: 15000,
  });

  // ── Active session detail ───────────────────────────────────────
  const detailQuery = useQuery<SessionDetail>({
    queryKey: ["/api/live-chat/sessions", activeSessionId],
    enabled: !!activeSessionId,
    refetchInterval: 10000,
  });

  // ── Supervisor data ─────────────────────────────────────────────
  useLiveMessageRead(activeSessionId, detailQuery.data?.messages);
  const supSessionsQuery = useQuery<SupervisorSession[]>({
    queryKey: ["/api/live-chat/supervisor/sessions"],
    enabled: supervisor,
    refetchInterval: 15000,
  });
  const supStatsQuery = useQuery<SupervisorStats>({
    queryKey: ["/api/live-chat/supervisor/stats"],
    enabled: supervisor,
    refetchInterval: 15000,
  });

  // ── Canned replies ──────────────────────────────────────────────
  const cannedQuery = useQuery<CannedReply[]>({
    queryKey: ["/api/live-chat/canned-replies"],
  });

  // ── Presence ────────────────────────────────────────────────────
  const presenceQuery = useQuery<{ status: string; note: string | null }>({
    queryKey: ["/api/live-chat/operator-status"],
  });
  const setPresence = useMutation({
    mutationFn: async (status: string) => {
      const res = await apiRequest("POST", "/api/live-chat/operator-status", { status });
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/live-chat/operator-status"] }),
    onError: (e: any) => {
      toast({ title: "Status unchanged", description: String(e?.message ?? e), variant: "destructive" });
      queryClient.invalidateQueries({ queryKey: ["/api/live-chat/operator-status"] });
    },
  });
  // An operator can't go busy/offline while a chat is still in progress.
  const hasOpenChat = (mineQuery.data || []).some(s => s.status === "active" || s.status === "escalated");

  // Presence heartbeat while online/away/busy/wrap-up.
  useEffect(() => {
    const status = presenceQuery.data?.status;
    if (!status || status === "offline") return;
    const i = window.setInterval(() => setPresence.mutate(status), 30000);
    return () => window.clearInterval(i);
  }, [presenceQuery.data?.status]);

  // Auto-online on first load when currently offline.
  useEffect(() => {
    if (presenceQuery.isLoading) return;
    if (presenceQuery.data?.status === "offline" || presenceQuery.data === undefined) {
      setPresence.mutate("online");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presenceQuery.isLoading]);

  // ── Real-time fanout ────────────────────────────────────────────
  useEffect(() => {
    const orgId = organization?.id;
    const meId = user?.id;
    if (!orgId && !meId) return;
    const token = (() => { try { return localStorage.getItem("dotnetbot.jwt") || ""; } catch { return ""; } })();
    const proto = window.location.protocol === "https:" ? "wss" : "ws";
    const base = `${proto}://${window.location.host}/ws/live-chat`;
    const sockets: WebSocket[] = [];
    const open = (topic: string, onEvent: (ev: string, payload: unknown) => void) => {
      try {
        const url = `${base}?topic=${encodeURIComponent(topic)}${token ? `&access_token=${encodeURIComponent(token)}` : ""}`;
        const ws = new WebSocket(url);
        ws.onmessage = (m) => {
          try { const env = JSON.parse(m.data); onEvent(env.event, env.payload); } catch { /* ignore */ }
        };
        sockets.push(ws);
      } catch { /* ignore */ }
    };
    if (orgId) {
      if (supervisor) {
        open(`supervisor:${orgId}`, () => {
          queryClient.invalidateQueries({ queryKey: ["/api/live-chat/supervisor/sessions"] });
          queryClient.invalidateQueries({ queryKey: ["/api/live-chat/supervisor/stats"] });
        });
      }
    }
    if (meId) {
      open(`operator:${meId}`, () => {
        queryClient.invalidateQueries({ queryKey: ["/api/live-chat/sessions/mine"] });
      });
    }
    return () => { for (const s of sockets) try { s.close(); } catch { /* ignore */ } };
  }, [user?.id, organization?.id, supervisor]);

  // Per-session sockets (transcript + internal notes).
  useEffect(() => {
    if (!activeSessionId) return;
    const token = (() => { try { return localStorage.getItem("dotnetbot.jwt") || ""; } catch { return ""; } })();
    const proto = window.location.protocol === "https:" ? "wss" : "ws";
    const base = `${proto}://${window.location.host}/ws/live-chat`;
    const sockets: WebSocket[] = [];
    const invalidate = () => queryClient.invalidateQueries({ queryKey: ["/api/live-chat/sessions", activeSessionId] });
    for (const topic of [`session:${activeSessionId}`, `notes:${activeSessionId}`]) {
      try {
        const url = `${base}?topic=${encodeURIComponent(topic)}${token ? `&access_token=${encodeURIComponent(token)}` : ""}`;
        const ws = new WebSocket(url);
        ws.onmessage = invalidate;
        sockets.push(ws);
      } catch { /* ignore */ }
    }
    return () => { for (const s of sockets) try { s.close(); } catch { /* ignore */ } };
  }, [activeSessionId]);


  // ── Escalation alert for supervisors ────────────────────────────
  useEffect(() => {
    if (!supervisor || !supSessionsQuery.data) return;
    const escalated = supSessionsQuery.data.filter(s => s.session.status === "escalated");
    const ids = new Set(escalated.map(s => s.session.id));
    const newOnes = escalated.filter(s => !lastEscIdsRef.current.has(s.session.id));
    if (lastEscIdsRef.current.size > 0 && newOnes.length > 0) {
      playAlertTone();
      try {
        if (typeof Notification !== "undefined" && Notification.permission === "granted") {
          new Notification("Chat escalated", {
            body: "An agent escalated a chat to a supervisor.",
          });
        }
      } catch { /* ignore */ }
    }
    lastEscIdsRef.current = ids;
  }, [supSessionsQuery.data, supervisor]);

  // ── New-message alert on the active session ─────────────────────
  // Fires when a visitor (or another operator) posts to the session
  // currently open. Suppressed while the tab is focused so an agent who
  // is actively reading the transcript isn't pinged on every reply.
  // Internal notes and the agent's own messages never alert.
  useEffect(() => {
    const msgs = detailQuery.data?.messages;
    const sid = detailQuery.data?.session?.id ?? activeSessionId ?? null;
    if (!msgs) return;
    const ids = new Set(msgs.map(m => m.id));
    // Seed the baseline silently on first load or when switching sessions.
    if (lastMsgSessionRef.current !== sid) {
      lastMsgSessionRef.current = sid;
      lastMsgIdsRef.current = ids;
      return;
    }
    // REST snapshots (including reconnect/login) never make sound or OS alerts.
    // Durable assigned-message unread state belongs to the existing bell.
    lastMsgIdsRef.current = ids;
  }, [detailQuery.data, user?.id, activeSessionId]);

  useEffect(() => {
    try {
      if (typeof Notification !== "undefined" && Notification.permission === "default") {
        Notification.requestPermission();
      }
    } catch { /* ignore */ }
  }, []);

  // ── Mutations ───────────────────────────────────────────────────
  const claimNext = useMutation({
    mutationFn: async () => {
      // Empty queue → backend replies 204 No Content (no JSON body).
      const url = deptFilter
        ? `/api/live-chat/sessions/claim-next?departmentId=${encodeURIComponent(deptFilter)}`
        : "/api/live-chat/sessions/claim-next";
      const res = await apiRequest("POST", url);
      if (res.status === 204) return null;
      return (await res.json()) as LiveChatSession;
    },
    onSuccess: (sess) => {
      queryClient.invalidateQueries({ queryKey: ["/api/live-chat/queue"] });
      queryClient.invalidateQueries({ queryKey: ["/api/live-chat/sessions/mine"] });
      if (sess?.id) { setMonitorOnly(false); setActiveSessionId(sess.id); toast({ title: "Chat claimed", description: `Connected to ${sess.visitorName ?? "the visitor"}.` }); }
      else toast({ title: "Queue empty", description: "No one is waiting right now." });
    },
    onError: (e: any) => toast({ title: "Could not claim", description: String(e?.message ?? e), variant: "destructive" }),
  });
  const claimSpecific = useMutation({
    mutationFn: async (id: string) => (await apiRequest("POST", `/api/live-chat/sessions/${id}/claim`)).json() as Promise<LiveChatSession>,
    onSuccess: (sess) => {
      queryClient.invalidateQueries({ queryKey: ["/api/live-chat/queue"] });
      queryClient.invalidateQueries({ queryKey: ["/api/live-chat/sessions/mine"] });
      setMonitorOnly(false); setActiveSessionId(sess.id);
    },
    onError: (e: any) => toast({ title: "Could not claim", description: String(e?.message ?? e), variant: "destructive" }),
  });
  const sendMessage = useMutation({
    mutationFn: async (content: string) => {
      if (!activeSessionId) throw new Error("No active session");
      return (await apiRequest("POST", `/api/live-chat/sessions/${activeSessionId}/messages`, { content })).json();
    },
    onSuccess: () => { setComposer(""); queryClient.invalidateQueries({ queryKey: ["/api/live-chat/sessions", activeSessionId] }); },
    onError: (e: any) => toast({ title: "Send failed", description: String(e?.message ?? e), variant: "destructive" }),
  });
  const addNote = useMutation({
    mutationFn: async (content: string) => {
      if (!activeSessionId) throw new Error("No active session");
      return (await apiRequest("POST", `/api/live-chat/sessions/${activeSessionId}/notes`, { content })).json();
    },
    onSuccess: () => { setNoteDraft(""); queryClient.invalidateQueries({ queryKey: ["/api/live-chat/sessions", activeSessionId] }); },
    onError: (e: any) => toast({ title: "Note failed", description: String(e?.message ?? e), variant: "destructive" }),
  });
  const uploadFile = useMutation({
    mutationFn: async (file: File) => {
      if (!activeSessionId) throw new Error("No active session");
      if (file.size > 5 * 1024 * 1024) throw new Error("File exceeds 5 MB limit");
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(file);
      });
      return (await apiRequest("POST", `/api/live-chat/sessions/${activeSessionId}/upload`, { dataUrl, fileName: file.name, mime: file.type })).json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/live-chat/sessions", activeSessionId] }),
    onError: (e: any) => toast({ title: "Upload failed", description: String(e?.message ?? e), variant: "destructive" }),
  });
  const escalate = useMutation({
    mutationFn: async (reason: string) => {
      if (!activeSessionId) throw new Error("No active session");
      return (await apiRequest("POST", `/api/live-chat/sessions/${activeSessionId}/escalate`, { reason })).json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/live-chat/sessions/mine"] });
      queryClient.invalidateQueries({ queryKey: ["/api/live-chat/sessions", activeSessionId] });
      toast({ title: "Escalated to supervisor" });
    },
    onError: (e: any) => toast({ title: "Escalation failed", description: String(e?.message ?? e), variant: "destructive" }),
  });
  const takeover = useMutation({
    mutationFn: async (id: string) => (await apiRequest("POST", `/api/live-chat/sessions/${id}/takeover`)).json() as Promise<LiveChatSession>,
    onSuccess: (sess) => {
      queryClient.invalidateQueries({ queryKey: ["/api/live-chat/sessions/mine"] });
      queryClient.invalidateQueries({ queryKey: ["/api/live-chat/supervisor/sessions"] });
      setMonitorOnly(false); setActiveSessionId(sess.id);
      toast({ title: "Took over chat" });
    },
    onError: (e: any) => toast({ title: "Take-over failed", description: String(e?.message ?? e), variant: "destructive" }),
  });
  const reassign = useMutation({
    mutationFn: async ({ id, agentId }: { id: string; agentId: string }) =>
      (await apiRequest("POST", `/api/live-chat/sessions/${id}/reassign`, { agentId })).json(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/live-chat/supervisor/sessions"] });
      queryClient.invalidateQueries({ queryKey: ["/api/live-chat/sessions/mine"] });
      toast({ title: "Chat reassigned" });
    },
    onError: (e: any) => toast({ title: "Reassign failed", description: String(e?.message ?? e), variant: "destructive" }),
  });
  const closeSession = useMutation({
    mutationFn: async (payload: { tag?: string; outcome?: string; note?: string }) => {
      if (!activeSessionId) throw new Error("No active session");
      return (await apiRequest("POST", `/api/live-chat/sessions/${activeSessionId}/close`, {
        dispositionTag: payload.tag, dispositionOutcome: payload.outcome, dispositionNote: payload.note,
      })).json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/live-chat/queue"] });
      queryClient.invalidateQueries({ queryKey: ["/api/live-chat/sessions/mine"] });
      queryClient.invalidateQueries({ queryKey: ["/api/live-chat/sessions", activeSessionId] });
      setShowDisposition(false);
      toast({ title: "Chat ended" });
    },
    onError: (e: any) => toast({ title: "Close failed", description: String(e?.message ?? e), variant: "destructive" }),
  });

  // Disposition modal state.
  const [showDisposition, setShowDisposition] = useState(false);
  const [dispTag, setDispTag] = useState("");
  const [dispOutcome, setDispOutcome] = useState("");
  const [dispNote, setDispNote] = useState("");

  // Auto-scroll transcript.
  const transcriptEndRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    transcriptEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [detailQuery.data?.messages.length]);

  // Auto-select first active session.
  useEffect(() => {
    if (!target.requestedSessionId && !activeSessionId && mineQuery.data && mineQuery.data.length > 0) {
      setActiveSessionId(mineQuery.data[0].id);
    }
  }, [mineQuery.data, activeSessionId, target.requestedSessionId]);

  useEffect(() => {
    if (target.queuedSessionId) highlightedQueueRef.current?.scrollIntoView({ block: "nearest" });
  }, [target.queuedSessionId, queueQuery.data]);

  const presenceColor = useMemo(() => {
    const s = presenceQuery.data?.status ?? "offline";
    if (s === "online") return "text-green-500";
    if (s === "away") return "text-yellow-500";
    if (s === "busy") return "text-red-500";
    if (s === "wrap-up") return "text-blue-500";
    return "text-gray-400";
  }, [presenceQuery.data?.status]);

  const detail = detailQuery.data;
  const session = detail?.session;
  const isMine = !!session && session.assignedOperatorId === user?.id;
  const canAct = !!session && session.status !== "closed" && (isMine || (supervisor && !monitorOnly));
  const escalatedSessions = (supSessionsQuery.data || []).filter(s => s.session.status === "escalated");
  const allActive = (supSessionsQuery.data || []).filter(s => s.session.status === "active");
  // Only truly online agents can receive routed work (matches the backend
  // presence gate); away/busy/wrap-up are not eligible reassign targets.
  const onlineAgents = (supStatsQuery.data?.agents || []).filter(a => a.status === "online");

  return (
    // Reflow (WCAG 1.4.10/1.4.12): below `md` the three panes stack vertically at
    // natural height (page scroll) so the main column is never squeezed by the
    // fixed-width rails; the fixed-height side-by-side layout applies from `md` up.
    <div className="flex flex-col md:flex-row md:h-[calc(100vh-3.5rem)] bg-background" data-testid="page-agent-console">
      {/* ─────── LEFT RAIL ─────── */}
      <aside className="w-full md:w-80 border-b md:border-b-0 md:border-r flex flex-col bg-gradient-to-b from-muted/30 to-transparent md:overflow-y-auto">
        <div className="p-4 border-b">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white shadow-sm" style={{ background: "linear-gradient(135deg, hsl(160 70% 45%), hsl(170 70% 40%))" }}>
              <Headphones className="w-4 h-4" />
            </div>
            <div className="min-w-0">
              <h2 className="text-sm font-semibold leading-tight">Agent Console</h2>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">
                {supervisor ? "Supervisor" : "Live agent"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Circle className={`w-3 h-3 fill-current ${presenceColor}`} />
            <select
              value={presenceQuery.data?.status ?? "offline"}
              onChange={(e) => setPresence.mutate(e.target.value)}
              className="flex-1 text-xs bg-transparent border rounded px-2 py-1 hover-elevate cursor-pointer"
              data-testid="select-presence"
            >
              <option value="online">Online</option>
              <option value="away">Away</option>
              <option value="busy" disabled={hasOpenChat}>Busy{hasOpenChat ? " (in chat)" : ""}</option>
              <option value="wrap-up">Wrap-up</option>
              <option value="offline" disabled={hasOpenChat}>Offline{hasOpenChat ? " (in chat)" : ""}</option>
            </select>
          </div>
        </div>

        {/* Queue */}
        <div className="p-3 border-b">
          {!activeSessionId && (target.message || target.isResolving) && (
            <div className="mb-3 rounded-md border bg-muted/40 p-2.5 text-xs">
              <p role="status">{target.isResolving ? "Checking request…" : target.message}</p>
              {!target.isResolving && !target.queuedSessionId && <Button variant="ghost" size="sm" className="mt-1" onClick={target.retry}>Try again</Button>}
            </div>
          )}
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">Queue</span>
            <Badge variant="secondary" data-testid="badge-queue-count">{queueQuery.data?.length ?? 0}</Badge>
          </div>
          {(deptListQuery.data?.length ?? 0) > 0 && (
            <select
              value={deptFilter}
              onChange={(e) => setDeptFilter(e.target.value)}
              className="w-full text-xs bg-transparent border rounded px-2 py-1 mb-2 hover-elevate cursor-pointer"
              data-testid="select-dept-filter"
            >
              <option value="">All departments</option>
              {deptListQuery.data!.map((d) => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
          )}
          <Button
            size="sm"
            className="w-full mb-2"
            onClick={() => claimNext.mutate()}
            disabled={!queueQuery.data?.length || claimNext.isPending}
            data-testid="button-claim-next"
          >
            {claimNext.isPending ? "Claiming…" : "Take next"}
          </Button>
          <div className="space-y-1 max-h-40 overflow-y-auto">
            {queueQuery.data?.length === 0 && (
              <p className="text-[11px] text-muted-foreground text-center py-3" data-testid="text-queue-empty">No one in queue</p>
            )}
            {queueQuery.data?.map((s) => (
              <button
                key={s.id}
                ref={s.id === target.queuedSessionId ? highlightedQueueRef : undefined}
                onClick={() => claimSpecific.mutate(s.id)}
                disabled={claimSpecific.isPending}
                className={`w-full text-left p-2 rounded-md text-xs hover-elevate ${s.id === target.queuedSessionId ? "bg-accent ring-2 ring-primary" : ""}`}
                aria-label={s.id === target.queuedSessionId ? "Claim highlighted request" : undefined}
                data-testid={`button-queue-${s.id}`}
              >
                <div className="flex items-center gap-1.5 font-medium truncate">
                  <UserIcon className="w-3 h-3 shrink-0" />{s.visitorName ?? "Visitor"}
                </div>
                <div className="flex items-center gap-1 text-muted-foreground mt-0.5">
                  <Clock className="w-3 h-3" />{relativeTime(s.requestedAt)}
                  {s.id === target.queuedSessionId && <span className="ml-auto font-medium text-primary">Claim</span>}
                </div>
                {deptName((s as any).departmentId) && (
                  <Badge variant="outline" className="mt-1 text-[10px] px-1.5 py-0" data-testid={`badge-queue-dept-${s.id}`}>
                    {deptName((s as any).departmentId)}
                  </Badge>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* My active */}
        <div className="p-3 border-b">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">My active</span>
            <Badge variant="outline" data-testid="badge-active-count">{mineQuery.data?.length ?? 0}</Badge>
          </div>
          <div className="space-y-1">
            {mineQuery.data?.length === 0 && (
              <p className="text-[11px] text-muted-foreground text-center py-3">No active chats</p>
            )}
            {mineQuery.data?.map((s) => (
              <button
                key={s.id}
                onClick={() => { setMonitorOnly(false); setActiveSessionId(s.id); }}
                className={`w-full text-left p-2 rounded-md text-xs hover-elevate ${activeSessionId === s.id ? "bg-accent" : ""}`}
                data-testid={`button-active-${s.id}`}
              >
                <div className="flex items-center gap-1.5 font-medium truncate">
                  <MessageSquare className="w-3 h-3 shrink-0" />{s.visitorName ?? "Visitor"}
                </div>
                <div className="text-muted-foreground mt-0.5">
                  {relativeTime(s.lastVisitorAt ?? s.lastOperatorAt ?? s.claimedAt)}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Supervisor panels */}
        {supervisor && (
          <>
            <div className="p-3 border-b">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] uppercase tracking-wider text-amber-600 font-medium flex items-center gap-1">
                  <ShieldAlert className="w-3 h-3" />Escalations
                </span>
                <Badge variant="secondary" data-testid="badge-escalation-count">{escalatedSessions.length}</Badge>
              </div>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {escalatedSessions.length === 0 && (
                  <p className="text-[11px] text-muted-foreground text-center py-3">No escalations</p>
                )}
                {escalatedSessions.map((s) => (
                  <div key={s.session.id} className="p-2 rounded-md text-xs border border-amber-300/40 bg-amber-50/40 dark:bg-amber-950/20" data-testid={`escalation-${s.session.id}`}>
                    <div className="font-medium truncate flex items-center gap-1">
                      <UserIcon className="w-3 h-3 shrink-0" />{s.session.visitorName ?? "Visitor"}
                    </div>
                    {s.session.escalationReason && (
                      <div className="text-muted-foreground mt-0.5 italic line-clamp-2">"{s.session.escalationReason}"</div>
                    )}
                    <div className="text-[10px] text-muted-foreground mt-0.5">
                      from {s.escalatedByName ?? "agent"} · {relativeTime(s.session.escalatedAt)}
                    </div>
                    <div className="flex gap-1 mt-1.5">
                      <Button size="sm" variant="default" className="h-6 text-[11px] px-2" onClick={() => takeover.mutate(s.session.id)} data-testid={`button-takeover-${s.session.id}`}>
                        <Zap className="w-3 h-3 mr-1" />Take over
                      </Button>
                      <Button size="sm" variant="ghost" className="h-6 text-[11px] px-2" onClick={() => { setMonitorOnly(true); setActiveSessionId(s.session.id); }} data-testid={`button-monitor-${s.session.id}`}>
                        <Eye className="w-3 h-3 mr-1" />Monitor
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="p-3 border-b">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">All active</span>
                <Badge variant="outline">{allActive.length}</Badge>
              </div>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {allActive.length === 0 && <p className="text-[11px] text-muted-foreground text-center py-3">No active chats</p>}
                {allActive.map((s) => (
                  <button
                    key={s.session.id}
                    onClick={() => { setMonitorOnly(s.session.assignedOperatorId !== user?.id); setActiveSessionId(s.session.id); }}
                    className={`w-full text-left p-2 rounded-md text-xs hover-elevate ${activeSessionId === s.session.id ? "bg-accent" : ""}`}
                    data-testid={`button-supervisor-active-${s.session.id}`}
                  >
                    <div className="font-medium truncate flex items-center gap-1">
                      <MessageSquare className="w-3 h-3 shrink-0" />{s.session.visitorName ?? "Visitor"}
                    </div>
                    <div className="text-[10px] text-muted-foreground mt-0.5">agent: {s.operatorName ?? "—"}</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium flex items-center gap-1">
                  <Users className="w-3 h-3" />Agents
                </span>
                {supStatsQuery.data && (
                  <span className="text-[10px] text-muted-foreground" data-testid="text-avg-wait">
                    avg wait {Math.round(supStatsQuery.data.avgWaitSeconds)}s
                  </span>
                )}
              </div>
              <div className="space-y-1">
                {onlineAgents.length === 0 && <p className="text-[11px] text-muted-foreground text-center py-3">No agents online</p>}
                {onlineAgents.map((a) => (
                  <div key={a.userId} className="flex items-center gap-2 p-1.5 rounded-md text-xs" data-testid={`agent-snapshot-${a.userId}`}>
                    <Circle className={`w-2.5 h-2.5 fill-current ${
                      a.status === "online" ? "text-green-500" : a.status === "away" ? "text-yellow-500"
                      : a.status === "busy" ? "text-red-500" : a.status === "wrap-up" ? "text-blue-500" : "text-gray-400"}`} />
                    <span className="truncate flex-1">{a.name}</span>
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0">{a.activeCount}</Badge>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </aside>

      {/* ─────── CENTRE ─────── */}
      <main className="flex-1 flex flex-col min-w-0 min-h-0">
        {!activeSessionId || !detail ? (
          <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground py-12 md:py-0">
            <div className="text-center">
              <Headphones className="w-12 h-12 mx-auto mb-3 opacity-40" />
              <p data-testid="text-empty-state">Take a chat from the queue to get started.</p>
            </div>
          </div>
        ) : (
          <>
            <div className="min-h-12 flex flex-wrap items-center justify-between px-4 py-2 border-b shrink-0 gap-2">
              <div className="min-w-0">
                <h3 className="text-sm font-semibold flex items-center gap-2" data-testid="text-active-visitor">
                  {session?.visitorName ?? "Visitor"}
                  {monitorOnly && <Badge variant="outline" className="text-[10px]"><Eye className="w-3 h-3 mr-1" />Monitoring</Badge>}
                  {session?.status === "escalated" && <Badge variant="secondary" className="text-[10px] text-amber-600"><ShieldAlert className="w-3 h-3 mr-1" />Escalated</Badge>}
                </h3>
                <p className="text-[11px] text-muted-foreground">
                  {session?.status} · {relativeTime(session?.claimedAt)}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                {/* Supervisor reassign */}
                {supervisor && session?.status !== "closed" && (
                  <select
                    className="text-xs bg-transparent border rounded px-2 py-1 hover-elevate cursor-pointer max-w-[140px]"
                    value=""
                    onChange={(e) => { if (e.target.value) reassign.mutate({ id: session!.id, agentId: e.target.value }); }}
                    data-testid="select-reassign"
                  >
                    <option value="">Reassign…</option>
                    {onlineAgents.filter(a => a.userId !== session?.assignedOperatorId).map(a => (
                      <option key={a.userId} value={a.userId}>{a.name}</option>
                    ))}
                  </select>
                )}
                {/* Escalate (agents on their own active chats) */}
                {isMine && session?.status === "active" && (
                  <Button size="sm" variant="outline" onClick={() => {
                    const reason = window.prompt("Reason for escalation (optional):") ?? "";
                    escalate.mutate(reason);
                  }} data-testid="button-escalate">
                    <ArrowRightLeft className="w-3.5 h-3.5 mr-1" />Escalate
                  </Button>
                )}
                {canAct && (
                  <Button size="sm" variant="outline" onClick={() => { setShowDisposition(true); setDispTag(""); setDispOutcome(""); setDispNote(""); }} data-testid="button-close-session">
                    <X className="w-3.5 h-3.5 mr-1" />End
                  </Button>
                )}
              </div>
            </div>

            <ScrollArea className="flex-1 min-h-0 max-h-[60vh] md:max-h-none px-4 py-3">
              <div className="max-w-2xl mx-auto space-y-3">
                {detail.messages.map((m) => (
                  <Bubble key={m.id} m={m} mineId={user?.id ?? ""} />
                ))}
                <div ref={transcriptEndRef} />
              </div>
            </ScrollArea>

            {/* Disposition modal */}
            {showDisposition && (
              <div className="border-t bg-muted/30 p-3 shrink-0" data-testid="panel-disposition">
                <div className="max-w-2xl mx-auto space-y-2">
                  <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Wrap-up disposition</div>
                  <div className="flex flex-wrap gap-2">
                    <select value={dispTag} onChange={(e) => setDispTag(e.target.value)} className="flex-1 text-xs border rounded px-2 py-1.5 bg-background" data-testid="select-disposition-tag">
                      <option value="">Tag…</option>
                      {DISPOSITION_TAGS.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                    <select value={dispOutcome} onChange={(e) => setDispOutcome(e.target.value)} className="flex-1 text-xs border rounded px-2 py-1.5 bg-background" data-testid="select-disposition-outcome">
                      <option value="">Outcome…</option>
                      {DISPOSITION_OUTCOMES.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                  <Input value={dispNote} onChange={(e) => setDispNote(e.target.value)} placeholder="Optional note…" data-testid="input-disposition-note" />
                  <div className="flex gap-2 justify-end">
                    <Button size="sm" variant="ghost" onClick={() => setShowDisposition(false)} data-testid="button-disposition-cancel">Cancel</Button>
                    <Button size="sm" onClick={() => closeSession.mutate({ tag: dispTag || undefined, outcome: dispOutcome || undefined, note: dispNote || undefined })} disabled={closeSession.isPending} data-testid="button-disposition-confirm">
                      End chat
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Canned replies tray */}
            {showCanned && canAct && (
              <div className="border-t bg-muted/30 p-3 shrink-0 max-h-48 overflow-y-auto" data-testid="panel-canned">
                <CannedRepliesTray
                  replies={cannedQuery.data || []}
                  onPick={(body) => { setComposer((c) => (c ? c + " " : "") + body); setShowCanned(false); }}
                />
              </div>
            )}

            {/* Notes tray */}
            {showNotes && (
              <div className="border-t bg-amber-50/40 dark:bg-amber-950/20 p-3 shrink-0" data-testid="panel-notes">
                <div className="max-w-2xl mx-auto flex gap-2">
                  <Input
                    value={noteDraft}
                    onChange={(e) => setNoteDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && noteDraft.trim()) { e.preventDefault(); addNote.mutate(noteDraft.trim()); } }}
                    placeholder="Internal note (never shown to visitor)…"
                    data-testid="input-note"
                  />
                  <Button size="sm" onClick={() => noteDraft.trim() && addNote.mutate(noteDraft.trim())} disabled={!noteDraft.trim() || addNote.isPending} data-testid="button-add-note">
                    <StickyNote className="w-4 h-4 mr-1" />Add
                  </Button>
                </div>
              </div>
            )}

            {/* Composer */}
            <div className="p-3 border-t shrink-0">
              <div className="max-w-2xl mx-auto flex flex-wrap gap-2">
                <input ref={fileInputRef} type="file" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadFile.mutate(f); e.target.value = ""; }} data-testid="input-file" />
                <IconButton variant="outline" label="Canned replies" onClick={() => setShowCanned(v => !v)} disabled={!canAct} data-testid="button-canned-toggle">
                  <MessageSquare className="w-4 h-4" aria-hidden="true" />
                </IconButton>
                <IconButton variant="outline" label="Internal notes" onClick={() => setShowNotes(v => !v)} data-testid="button-notes-toggle">
                  <StickyNote className="w-4 h-4" aria-hidden="true" />
                </IconButton>
                <IconButton variant="outline" label="Attach file" onClick={() => fileInputRef.current?.click()} disabled={uploadFile.isPending || !canAct} data-testid="button-attach">
                  <Paperclip className="w-4 h-4" aria-hidden="true" />
                </IconButton>
                <Input
                  value={composer}
                  onChange={(e) => setComposer(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); if (composer.trim()) sendMessage.mutate(composer.trim()); } }}
                  placeholder={canAct ? "Type a reply…" : monitorOnly ? "Monitoring — take over to reply" : "Read-only"}
                  disabled={!canAct}
                  className="flex-1 min-w-[10rem]"
                  data-testid="input-composer"
                />
                <Button onClick={() => composer.trim() && sendMessage.mutate(composer.trim())} disabled={!composer.trim() || sendMessage.isPending || !canAct} data-testid="button-send">
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </main>

      {/* ─────── RIGHT RAIL ─────── */}
      {activeSessionId && detail && (
        <aside className="w-full md:w-72 border-t md:border-t-0 md:border-l md:overflow-y-auto p-4 space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground">Visitor</CardTitle>
            </CardHeader>
            <CardContent className="text-xs space-y-1.5">
              <Field icon={UserIcon} label="Name" value={session?.visitorName ?? "Anonymous"} testId="text-visitor-name" />
              <Field icon={Mail} label="Email" value={session?.visitorEmail ?? "—"} testId="text-visitor-email" />
              <Field icon={Globe} label="IP" value={session?.visitorIp ?? "—"} testId="text-visitor-ip" />
              <Field icon={Globe} label="Country" value={session?.visitorCountry ?? "Unknown"} testId="text-visitor-country" />
              <Field icon={MonitorSmartphone} label="Browser" value={shortenUA(session?.visitorUserAgent ?? null)} testId="text-visitor-ua" />
            </CardContent>
          </Card>

          {(session?.dispositionTag || session?.dispositionOutcome) && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground">Disposition</CardTitle>
              </CardHeader>
              <CardContent className="text-xs space-y-1.5">
                {session?.dispositionTag && <Field icon={MessageSquare} label="Tag" value={session.dispositionTag} />}
                {session?.dispositionOutcome && <Field icon={MessageSquare} label="Outcome" value={session.dispositionOutcome} />}
                {session?.dispositionNote && <Field icon={StickyNote} label="Note" value={session.dispositionNote} />}
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground">Bot transcript</CardTitle>
            </CardHeader>
            <CardContent className="text-xs space-y-2 max-h-80 overflow-y-auto">
              {detail.botTranscript.length === 0 && <p className="text-muted-foreground">No prior bot conversation.</p>}
              {detail.botTranscript.map((m, i) => (
                <div key={i}>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{m.role}</div>
                  <div className="whitespace-pre-wrap">{m.content}</div>
                </div>
              ))}
            </CardContent>
          </Card>
        </aside>
      )}
    </div>
  );
}

function CannedRepliesTray({ replies, onPick }: { replies: CannedReply[]; onPick: (body: string) => void }) {
  const { toast } = useToast();
  const [adding, setAdding] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const create = useMutation({
    mutationFn: async () => (await apiRequest("POST", "/api/live-chat/canned-replies", { title, body })).json(),
    onSuccess: () => { setTitle(""); setBody(""); setAdding(false); queryClient.invalidateQueries({ queryKey: ["/api/live-chat/canned-replies"] }); },
    onError: (e: any) => toast({ title: "Could not save", description: String(e?.message ?? e), variant: "destructive" }),
  });
  const remove = useMutation({
    mutationFn: async (id: string) => (await apiRequest("DELETE", `/api/live-chat/canned-replies/${id}`)).json(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/live-chat/canned-replies"] }),
  });
  return (
    <div className="max-w-2xl mx-auto space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Canned replies</span>
        <Button size="sm" variant="ghost" className="h-6 text-[11px]" onClick={() => setAdding(v => !v)} data-testid="button-canned-add">
          <Plus className="w-3 h-3 mr-1" />New
        </Button>
      </div>
      {adding && (
        <div className="space-y-1.5 border rounded-md p-2 bg-background">
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title" className="h-7 text-xs" data-testid="input-canned-title" />
          <Textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Reply body…" className="text-xs min-h-16" data-testid="input-canned-body" />
          <div className="flex justify-end">
            <Button size="sm" className="h-6 text-[11px]" onClick={() => create.mutate()} disabled={!title.trim() || !body.trim() || create.isPending} data-testid="button-canned-save">Save</Button>
          </div>
        </div>
      )}
      <div className="space-y-1">
        {replies.length === 0 && !adding && <p className="text-[11px] text-muted-foreground text-center py-2">No canned replies yet.</p>}
        {replies.map((r) => (
          <div key={r.id} className="flex items-start gap-2 p-1.5 rounded-md hover-elevate group" data-testid={`canned-${r.id}`}>
            <button className="text-left flex-1 min-w-0" onClick={() => onPick(r.body)} data-testid={`button-canned-pick-${r.id}`}>
              <div className="text-xs font-medium truncate">{r.title}</div>
              <div className="text-[11px] text-muted-foreground line-clamp-1">{r.body}</div>
            </button>
            <button className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive" onClick={() => remove.mutate(r.id)} data-testid={`button-canned-delete-${r.id}`} title={`Deactivate canned reply ${r.title}`} aria-label={`Deactivate canned reply ${r.title}`}>
              <Trash2 className="w-3.5 h-3.5" aria-hidden="true" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function Bubble({ m, mineId }: { m: LiveChatMessage; mineId: string }) {
  if ((m as any).internal) {
    return (
      <div className="flex justify-center" data-testid={`msg-note-${m.id}`}>
        <div className="max-w-[85%] rounded-md px-3 py-1.5 text-xs bg-amber-100 dark:bg-amber-950/40 text-amber-900 dark:text-amber-200 border border-amber-300/50 flex items-start gap-1.5">
          <StickyNote className="w-3 h-3 mt-0.5 shrink-0" />
          <div>
            <div className="whitespace-pre-wrap">{m.content}</div>
            <div className="text-[10px] opacity-60 mt-0.5">internal note · {relativeTime(m.createdAt)}</div>
          </div>
        </div>
      </div>
    );
  }
  if (m.sender === "system") {
    return (
      <div className="text-center text-[11px] text-muted-foreground italic" data-testid={`msg-system-${m.id}`}>
        {m.content}
      </div>
    );
  }
  const mine = m.sender === "operator" && (m.operatorId === mineId || !mineId);
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.sender}-${m.id}`}>
      <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${mine ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
        {m.content && <div className="whitespace-pre-wrap">{m.content}</div>}
        {m.attachmentUrl && (
          <div className="mt-1">
            {m.attachmentMime?.startsWith("image/") ? (
              <img src={m.attachmentUrl} alt={m.attachmentName ?? "attachment"} className="max-w-full max-h-64 rounded-md" />
            ) : (
              <a href={m.attachmentUrl} download={m.attachmentName ?? "file"} className="underline text-xs">{m.attachmentName ?? "Download file"}</a>
            )}
          </div>
        )}
        <div className="text-[10px] opacity-60 mt-1">{relativeTime(m.createdAt)}</div>
      </div>
    </div>
  );
}

function Field({ icon: Icon, label, value, testId }: { icon: any; label: string; value: string; testId?: string }) {
  return (
    <div className="flex items-start gap-1.5">
      <Icon className="w-3 h-3 mt-0.5 text-muted-foreground shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
        <div className="truncate" data-testid={testId}>{value}</div>
      </div>
    </div>
  );
}

function shortenUA(ua: string | null): string {
  if (!ua) return "—";
  let browser = "Browser";
  if (ua.includes("Edg/")) browser = "Edge";
  else if (ua.includes("Chrome/")) browser = "Chrome";
  else if (ua.includes("Firefox/")) browser = "Firefox";
  else if (ua.includes("Safari/")) browser = "Safari";
  let os = "";
  if (ua.includes("Mac OS X")) os = "macOS";
  else if (ua.includes("Windows")) os = "Windows";
  else if (ua.includes("Linux")) os = "Linux";
  else if (ua.includes("Android")) os = "Android";
  else if (ua.includes("iPhone") || ua.includes("iPad")) os = "iOS";
  return os ? `${browser} on ${os}` : browser;
}
